// components/gifts/LuxurySetBanner.jsx
import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';

const LuxurySetBanner = () => {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, amount: 0.3 });

    return (
        <section ref={ref} className="py-20 bg-gradient-to-br from-ivory via-white to-nude-beige">
            <div className="container mx-auto px-6 lg:px-12">
                <div className="max-w-6xl mx-auto">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <motion.div
                            initial={{ x: -60, opacity: 0 }}
                            animate={isInView ? { x: 0, opacity: 1 } : {}}
                            transition={{ duration: 1.2 }}
                            className="lg:pr-12"
                        >
                            <h2 className="font-playfair text-4xl md:text-5xl text-charcoal mb-6">
                                The Art of Gifting
                            </h2>
                            <p className="font-poppins text-lg text-charcoal/70 mb-8 leading-relaxed">
                                Each KROMAIC gift set is more than a collection of products—it's a carefully
                                curated experience. From our signature embossed packaging to the silk ribbon
                                detailing, every element is designed to elevate the ritual of giving.
                            </p>

                            <div className="space-y-4">
                                {[
                                    "Handcrafted presentation boxes",
                                    "Personalized message cards",
                                    "Ritual guide with expert tips",
                                    "Sustainable, reusable packaging"
                                ].map((feature, index) => (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, x: -20 }}
                                        animate={isInView ? { opacity: 1, x: 0 } : {}}
                                        transition={{ duration: 0.5, delay: 0.8 + index * 0.1 }}
                                        className="flex items-center gap-3"
                                    >
                                        <div className="w-2 h-2 bg-sage-green rounded-full" />
                                        <span className="font-poppins text-charcoal/80">{feature}</span>
                                    </motion.div>
                                ))}
                            </div>

                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={isInView ? { opacity: 1, y: 0 } : {}}
                                transition={{ duration: 0.8, delay: 1.2 }}
                                className="mt-12"
                            >
                                <motion.button
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.98 }}
                                    className="px-8 py-3 bg-transparent border-2 border-sage-green text-sage-green
                    font-poppins rounded-full hover:bg-sage-green hover:text-white 
                    transition-all duration-300"
                                >
                                    Explore Gift Services
                                </motion.button>
                            </motion.div>
                        </motion.div>

                        <motion.div
                            initial={{ x: 60, opacity: 0 }}
                            animate={isInView ? { x: 0, opacity: 1 } : {}}
                            transition={{ duration: 1.2 }}
                            className="relative"
                        >
                            <div className="relative rounded-2xl overflow-hidden shadow-luxe">
                                <img
                                    src="/images/luxury-gift-box.jpg"
                                    alt="KROMAIC Luxury Gift Box"
                                    className="w-full h-[400px] object-cover"
                                />
                                <motion.div
                                    initial={{ scale: 1.1 }}
                                    animate={isInView ? { scale: 1 } : {}}
                                    transition={{ duration: 1.5 }}
                                    className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"
                                />
                            </div>

                            <motion.div
                                initial={{ opacity: 0, y: 40 }}
                                animate={isInView ? { opacity: 1, y: 0 } : {}}
                                transition={{ duration: 0.8, delay: 0.6 }}
                                className="absolute -bottom-6 -right-6 bg-white p-6 rounded-2xl shadow-luxe max-w-xs"
                            >
                                <div className="text-sage-green text-3xl mb-2">🎁</div>
                                <h4 className="font-cormorant text-xl text-charcoal mb-2">
                                    Complimentary Gift Wrapping
                                </h4>
                                <p className="font-poppins text-sm text-charcoal/70">
                                    All gift sets include our signature wrapping at no additional cost
                                </p>
                            </motion.div>
                        </motion.div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default LuxurySetBanner;